import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import {
  BuildingOffice, EnvelopeSimple, Globe, Copy, CheckCircle, ArrowRight, RocketLaunch, Code, Image as ImageIcon,
} from "@phosphor-icons/react";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [orgName, setOrgName] = useState(user?.org_name || "");
  const [saving, setSaving] = useState(false);

  const [badge, setBadge] = useState(null);
  const [badgeToggling, setBadgeToggling] = useState(false);
  const [copied, setCopied] = useState(null); // 'url' | 'embed' | null
  const [badgeDesc, setBadgeDesc] = useState("");
  const [showScore, setShowScore] = useState(true);
  const [showCounts, setShowCounts] = useState(true);
  const [badgeTheme, setBadgeTheme] = useState("dark");
  const [savingCfg, setSavingCfg] = useState(false);

  const [onboardingStatus, setOnboardingStatus] = useState(null);

  useEffect(() => {
    api.get("/settings/badge").then((r) => {
      setBadge(r.data);
      setBadgeDesc(r.data.description || "");
      setShowScore(r.data.show_score !== false);
      setShowCounts(r.data.show_counts !== false);
    });
    api.get("/onboarding/status").then((r) => setOnboardingStatus(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/settings/org", { org_name: orgName });
      setUser({ ...user, org_name: data.org_name });
      toast.success("Organization updated");
    } catch (_) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleBadge = async (enabled) => {
    setBadgeToggling(true);
    try {
      const { data } = await api.post("/settings/badge", { enabled });
      setBadge(data);
      toast.success(enabled ? "Public widget enabled" : "Public widget disabled");
    } catch (_) {
      toast.error("Failed to update widget");
    } finally {
      setBadgeToggling(false);
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.post("/settings/badge", {
        enabled: badge?.public_badge_enabled,
        description: badgeDesc,
        show_score: showScore,
        show_counts: showCounts,
      });
      setBadge(data);
      toast.success("Widget settings saved");
    } catch (_) {
      toast.error("Save failed");
    } finally {
      setSavingCfg(false);
    }
  };

  const publicUrl = badge?.public_slug ? `${window.location.origin}/o/${badge.public_slug}` : null;
  const embedSnippet = badge?.public_slug ? `<!-- AuditReady Sports Audit-Readiness Badge -->
<a href="${window.location.origin}/o/${badge.public_slug}" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none;">
  <img src="${API}/public/badge/${badge.public_slug}/image.svg?theme=${badgeTheme}"
       alt="Audit Readiness Badge for ${badge.org_name || ""}"
       width="320" height="120" style="border:0;max-width:100%;height:auto;" />
</a>
<!-- /AuditReady Sports Badge -->` : "";

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied("url");
      setTimeout(() => setCopied(null), 1800);
    } catch (_) { toast.error("Copy failed"); }
  };

  const copyEmbed = async () => {
    if (!embedSnippet) return;
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied("embed");
      setTimeout(() => setCopied(null), 1800);
    } catch (_) { toast.error("Copy failed"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="font-label text-orange-400 mb-2">SETTINGS</div>
        <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Your Account</h1>
      </div>

      <div className="rounded-2xl border border-white/10 bg-slate-900 p-6 mb-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border border-white/10">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-slate-800 text-slate-200 font-bold">
              {(user?.name || "?").split(" ").map(n=>n[0]).slice(0,2).join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-xl font-display font-black tracking-tight">{user?.name}</div>
            <div className="text-sm text-slate-400 flex items-center gap-2">
              <EnvelopeSimple size={14} weight="bold" />{user?.email}
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding resume */}
      {onboardingStatus && !onboardingStatus.completed && (
        <Link
          to="/onboarding"
          data-testid="resume-onboarding-link"
          className="block mb-6 rounded-2xl border border-orange-500/30 bg-orange-500/5 p-5 hover:bg-orange-500/10 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <RocketLaunch size={22} weight="bold" className="text-orange-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-100">Finish setup</div>
                <div className="text-xs text-slate-400 mt-1">You're {onboardingStatus.progress_pct}% through onboarding. Pick up where you left off.</div>
              </div>
            </div>
            <ArrowRight size={16} weight="bold" className="text-orange-400 shrink-0" />
          </div>
        </Link>
      )}

      <form onSubmit={save} className="rounded-2xl border border-white/10 bg-slate-900 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <BuildingOffice size={18} weight="bold" className="text-orange-400" />
          <h2 className="font-display font-black tracking-tight text-lg">Organization</h2>
        </div>
        <Label className="font-label text-slate-400">ORGANIZATION NAME</Label>
        <Input
          data-testid="org-name-input"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          className="bg-slate-950 border-white/10 focus:border-orange-500 mt-1"
          placeholder="Lincoln High Athletics"
        />
        <p className="text-xs text-slate-500 mt-2">Appears on compliance reports and PDF exports.</p>
        <Button
          data-testid="save-settings-button"
          type="submit"
          disabled={saving}
          className="mt-5 bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </form>

      {/* Public badge */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Globe size={18} weight="bold" className="text-orange-400" />
            <h2 className="font-display font-black tracking-tight text-lg">Public Audit-Readiness Widget</h2>
          </div>
          <Switch
            data-testid="public-badge-toggle"
            checked={!!badge?.public_badge_enabled}
            disabled={badgeToggling}
            onCheckedChange={toggleBadge}
          />
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Optionally share a public page + embeddable badge showing your Audit Readiness Score.
          <span className="text-slate-300 font-semibold"> No PII is ever exposed</span> — no staff names, credential numbers, documents, medical or background-check data.
        </p>

        {badge?.public_badge_enabled && publicUrl ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-4" data-testid="public-badge-url-box">
              <div className="font-label text-orange-400 mb-2">PROFILE PAGE URL</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-slate-950 border border-white/10 px-3 py-2 text-sm text-slate-200 truncate">
                  {publicUrl}
                </code>
                <Button
                  data-testid="copy-badge-url"
                  type="button"
                  onClick={copyLink}
                  className="bg-slate-800 border border-white/10 text-slate-100 hover:bg-slate-700 rounded-lg"
                >
                  {copied === "url" ? <CheckCircle size={14} weight="bold" className="text-emerald-400" /> : <Copy size={14} weight="bold" />}
                </Button>
                <Link
                  to={`/o/${badge.public_slug}`}
                  target="_blank"
                  className="rounded-lg bg-orange-500 text-white hover:bg-orange-400 px-3 py-2 text-sm font-semibold"
                >
                  Preview
                </Link>
              </div>
            </div>

            {/* Badge image preview with theme selector */}
            <div className="rounded-xl border border-white/10 bg-slate-950 p-5" data-testid="badge-image-box">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <ImageIcon size={16} weight="bold" className="text-orange-400" />
                  <div className="font-label text-slate-400">BADGE IMAGE</div>
                </div>
                <div className="inline-flex rounded-full border border-white/10 bg-slate-900 p-1 text-xs" data-testid="badge-theme-toggle">
                  {["dark", "light"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`badge-theme-${t}`}
                      onClick={() => setBadgeTheme(t)}
                      className={`px-3 py-1 rounded-full font-semibold capitalize transition-colors ${badgeTheme === t ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-100"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <img
                  key={badgeTheme}
                  src={`${API}/public/badge/${badge.public_slug}/image.svg?theme=${badgeTheme}&t=${Date.now()}`}
                  alt="Badge preview"
                  className="rounded-lg border border-white/10"
                  data-testid="badge-image-preview"
                />
                <a
                  href={`${API}/public/badge/${badge.public_slug}/image.svg?theme=${badgeTheme}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-orange-300 hover:text-orange-200"
                >
                  Direct link →
                </a>
              </div>
            </div>

            {/* Embed code */}
            <div className="rounded-xl border border-white/10 bg-slate-950 p-5" data-testid="embed-code-box">
              <div className="flex items-center gap-2 mb-3">
                <Code size={16} weight="bold" className="text-orange-400" />
                <div className="font-label text-slate-400">EMBED HTML</div>
              </div>
              <pre className="text-xs text-slate-300 bg-slate-900 rounded-lg p-3 overflow-x-auto border border-white/5 whitespace-pre-wrap break-all">
{embedSnippet}
              </pre>
              <Button
                data-testid="copy-embed-code"
                type="button"
                onClick={copyEmbed}
                className="mt-3 bg-slate-800 border border-white/10 text-slate-100 hover:bg-slate-700 rounded-lg"
              >
                {copied === "embed" ? <><CheckCircle size={14} weight="bold" className="mr-1.5 text-emerald-400" /> Copied</> : <><Copy size={14} weight="bold" className="mr-1.5" /> Copy embed code</>}
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                Paste anywhere on your league or school website. The badge auto-updates every 5 minutes.
              </p>
            </div>

            {/* Visibility config */}
            <div className="rounded-xl border border-white/10 bg-slate-950 p-5" data-testid="badge-visibility-box">
              <div className="font-label text-slate-400 mb-3">VISIBILITY SETTINGS</div>

              <div className="space-y-4">
                <div>
                  <Label className="font-label text-slate-400">SHORT DESCRIPTION (optional)</Label>
                  <Textarea
                    data-testid="badge-description-input"
                    value={badgeDesc}
                    onChange={(e) => setBadgeDesc(e.target.value.slice(0, 280))}
                    placeholder="e.g. Home of Lincoln Athletics — SafeSport-first, coach-first."
                    className="bg-slate-900 border-white/10 focus:border-orange-500 mt-1 min-h-[70px]"
                  />
                  <div className="text-xs text-slate-500 mt-1">{badgeDesc.length}/280 · Not PII. Public visitors will see this.</div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">Show readiness score</div>
                    <div className="text-xs text-slate-500">Big percentage on your public page.</div>
                  </div>
                  <Switch
                    data-testid="badge-show-score-toggle"
                    checked={!!showScore}
                    onCheckedChange={setShowScore}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">Show credential counts</div>
                    <div className="text-xs text-slate-500">Active / expiring / total credentials.</div>
                  </div>
                  <Switch
                    data-testid="badge-show-counts-toggle"
                    checked={!!showCounts}
                    onCheckedChange={setShowCounts}
                  />
                </div>
              </div>

              <Button
                data-testid="save-badge-config"
                onClick={saveConfig}
                disabled={savingCfg}
                className="mt-5 bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
              >
                {savingCfg ? "Saving…" : "Save widget settings"}
              </Button>
            </div>

            <p className="text-xs text-slate-500">
              Turn the toggle off at any time to instantly make this page + widget inaccessible.
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Toggle on above to generate a shareable link, badge image, and embed code.</p>
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-6 rounded-xl border border-white/10 bg-slate-900/70 p-5" data-testid="settings-disclaimer">
        <div className="font-label text-slate-500 mb-2">LEGAL</div>
        <p className="text-xs text-slate-500 leading-relaxed">
          AuditReady Sports is an independent record-keeping platform. It is <span className="text-slate-400 font-semibold">not affiliated with, endorsed by, or sponsored by</span> FIFA,
          U.S. Soccer (USSF), the U.S. Center for SafeSport, NFHS, the Board of Certification (BOC), or any other governing sports body.
          Credential names are used for organizational reference only. We do not verify credentials with issuing bodies — administrators remain responsible for confirming
          validity with the issuing organization. The Audit Readiness Score reflects only records tracked in this system and is not a legal
          certification of compliance.
        </p>
      </div>
    </div>
  );
}
